import type { AuthUser, UserRole } from '@sfa/shared';
import { create } from 'zustand';
import {
  api, apiGet, apiPost, clearSession, restoreSession, setAccessToken, setUnauthenticatedHandler,
} from '@/lib/api';

/**
 * Authentication state.
 *
 * `status` is a three-state machine rather than a boolean: on a hard refresh
 * the app must show a loading state while it tries to restore the session from
 * the refresh cookie. With only `isAuthenticated`, that moment reads as
 * "logged out" and bounces the user to the login screen every time they hit F5.
 */
export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

interface AuthState {
  user: AuthUser | null;
  status: AuthStatus;
  login: (identifier: string, password: string) => Promise<AuthUser>;
  logout: () => Promise<void>;
  restore: () => Promise<void>;
  setUser: (user: AuthUser | null) => void;
  hasRole: (...roles: UserRole[]) => boolean;
}

export const useAuth = create<AuthState>((set, get) => ({
  user: null,
  status: 'loading',

  async login(identifier, password) {
    const result = await apiPost<{ user: AuthUser; accessToken: string; expiresIn: number }>(
      '/auth/login',
      { identifier, password },
    );

    setAccessToken(result.accessToken);
    set({ user: result.user, status: 'authenticated' });

    return result.user;
  },

  async logout() {
    try {
      await api.post('/auth/logout');
    } catch {
      // A failed logout call must not trap the user in the app; the local
      // session is cleared regardless.
    }

    // clearSession(), not setAccessToken(null): it also voids a restore that
    // may already be in flight, so it cannot resolve afterwards and silently
    // sign this tab back in.
    clearSession();
    set({ user: null, status: 'unauthenticated' });
  },

  /**
   * Restore the session on app boot.
   *
   * The access token lives only in memory, so after a refresh there is none.
   * The refresh cookie survives, so exchanging it silently re-establishes the
   * session without the user seeing a login screen.
   *
   * Goes through the shared restoreSession() rather than its own
   * `/auth/refresh` call: the api-client request interceptor joins this same
   * in-flight promise for any protected request that lands while it is
   * running, instead of firing unauthenticated and racing it.
   */
  async restore() {
    const result = await restoreSession();

    if (result) {
      set({ user: result.user, status: 'authenticated' });
    } else {
      setAccessToken(null);
      set({ user: null, status: 'unauthenticated' });
    }
  },

  setUser(user) {
    set({ user, status: user ? 'authenticated' : 'unauthenticated' });
  },

  hasRole(...roles) {
    const role = get().user?.role;

    return role !== undefined && roles.includes(role);
  },
}));

// When a refresh finally fails, drop the session so the router redirects.
setUnauthenticatedHandler(() => {
  useAuth.setState({ user: null, status: 'unauthenticated' });
});

/** Re-reads the profile — used after a change that alters permissions. */
export async function refreshProfile(): Promise<void> {
  const user = await apiGet<AuthUser>('/auth/me');
  useAuth.getState().setUser(user);
}
